import React, { useState } from 'react';
import Styled from 'styled';
import { useQuery } from '@apollo/client/react/hooks';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { Link } from 'react-router-dom';

import ImageView from 'lib/images/image-view';
import btoa from 'lib/base64/btoa';
import atob from 'lib/base64/atob';

import { Site } from 'four-nets/site';
import TextShortener from 'four-nets/ui-v2/text-shortener/text-shortener';

import ArrowWidgetButton from '../arrow-widget-button';

import query, { BazaarWidgetQuery, BazaarWidgetQueryVariables } from './bazaar-widget.graphql';
import defaultStyles from './bazaar-widget.scss';

interface Props {
    styles: typeof defaultStyles;
    categoryIds: string[];
}

const BazaarWidget: React.FC<Props> = (props, context) => {
    const { styles, categoryIds } = props;
    const site = context && context.site;

    const [after, setAfter] = useState<null | string>(null);

    const { data } = useQuery<BazaarWidgetQuery, BazaarWidgetQueryVariables>(query, {
        variables: { categoryIds, first: 2, after },
        fetchPolicy: 'cache-first',
    });

    const endCursor = data && data.productsDb && data.productsDb.pageInfo.endCursor;
    const startCursor = data && data.productsDb && data.productsDb.pageInfo.startCursor;

    const startIndex = (startCursor && parseInt(atob(startCursor).split(':')[1], 10)) || 0;
    const hasPreviousPage = startIndex > 0;
    const hasNextPage = data && data.productsDb && data.productsDb.pageInfo.hasNextPage;

    const prevPageAfter = startIndex < 3 ? null : btoa(`arrayconnection:${startIndex - 3}`);

    if (data && data.productsDb && data.productsDb.edges) {
        return (
            <div className={styles.widget}>
                <div className={styles.widgetTitle}>
                    <FormattedMessage id='IN_MARKET_ON_THIS_TOPIC' />
                </div>
                <div className={styles.images}>
                    <ArrowWidgetButton
                        onClick={() => {
                            setAfter(prevPageAfter || null);
                        }}
                        direction='left'
                        hidden={!hasPreviousPage}
                        onlyDesktop={true}
                    />
                    {data.productsDb.edges.map(edge =>
                        edge && edge.node ? (
                            <div className={styles.imageWrapper}>
                                <Link to={edge.node.url || ''} className={styles.image}>
                                    <ImageView
                                        aspectFill={true}
                                        linkify={false}
                                        {...(edge.node.featuredImage as any)}
                                    />
                                </Link>
                                <div className={styles.productInfo}>
                                    <div className={styles.productInfo1}>
                                        <TextShortener text={edge.node.title} textLength={40}>
                                            {text => (
                                                <>
                                                    <div className={styles.title}>{text}</div>
                                                    <div className={styles.description}>
                                                        <TextShortener
                                                            text={edge?.node?.description}
                                                            textLength={edge?.node?.title === text ? 75 : 50}
                                                        />
                                                    </div>
                                                </>
                                            )}
                                        </TextShortener>
                                    </div>

                                    <div className={styles.price}>
                                        {edge.node.price} {site && site.currency}
                                    </div>
                                </div>
                            </div>
                        ) : null
                    )}
                    <ArrowWidgetButton
                        onlyDesktop={true}
                        onClick={() => {
                            setAfter(endCursor || null);
                        }}
                        direction='right'
                        hidden={!hasNextPage}
                    />
                </div>
            </div>
        );
    }

    return null;
};

BazaarWidget.contextTypes = {
    site: PropTypes.instanceOf(Site),
};

export default Styled(defaultStyles)(BazaarWidget);
